/**
 * Warm the work-order PDFs into the offline cache (Issue #719).
 *
 * The service worker ALREADY CacheFirsts `/api/attachments/:id/download` into
 * the `gwc-attachments` cache (client/src/sw.ts), so the bytes survive going
 * offline — but nothing fetches them until the tech taps the file, and in a
 * mechanical room with no signal that is too late. This warms them while the
 * job screen still has a connection.
 *
 * THE `disposition=inline` IS LOAD-BEARING. The bare download path 302s to a
 * presigned S3 URL — a cross-origin, opaque response that will not cache
 * reliably. `disposition=inline` returns a same-origin proxied 200 that the
 * existing CacheFirst route stores cleanly.
 *
 * Only the work orders are warmed, not every photo: the SW's ExpirationPlugin
 * caps `gwc-attachments` at 500 entries, and evicting a tech's other jobs to
 * pre-cache thumbnails they may never open is a bad trade.
 */
import { attachmentDownloadUrl } from "@/lib/api";
import type { JobFile } from "./job-files";

const WARM_KINDS = new Set(["original_work_order", "completed_work_order", "work_order_scan"]);

/** Cache name must match the CacheFirst route in client/src/sw.ts. */
const ATTACHMENT_CACHE = "gwc-attachments";

/** The files worth holding on the device for a no-signal job. */
export function filesToWarm(files: JobFile[]): JobFile[] {
    return files.filter((f) => WARM_KINDS.has(f.kind));
}

/**
 * The SW strips `?token=` from the cache key (the token rotates on every login,
 * and keying on it invalidated every cached file). Match that here so a lookup
 * finds what the SW actually stored.
 */
function cacheKeyFor(id: string): string {
    const url = new URL(attachmentDownloadUrl(id, { disposition: "inline" }), window.location.origin);
    url.searchParams.delete("token");
    return url.href;
}

/**
 * Which of these files are already readable with no signal. Returns an empty
 * set when the Cache API is unavailable — an honest "we don't know" reads as
 * "not saved", which is the safe direction: never tell a tech a file is
 * available offline when it might not be.
 */
export async function readOfflineReadyIds(files: JobFile[]): Promise<Set<string>> {
    const ready = new Set<string>();
    if (typeof caches === "undefined") return ready;
    try {
        const cache = await caches.open(ATTACHMENT_CACHE);
        await Promise.all(
            filesToWarm(files).map(async (f) => {
                const hit = await cache.match(cacheKeyFor(f.id));
                if (hit) ready.add(f.id);
            }),
        );
    } catch {
        // A blocked/absent Cache API is not an error worth surfacing — the
        // files still work online, we just can't promise they work offline.
    }
    return ready;
}

/**
 * Fetch the job's work orders so the service worker stores them. Best-effort:
 * a failure here must never surface to the tech or block the job screen.
 */
export async function warmJobFiles(files: JobFile[]): Promise<void> {
    await Promise.all(
        filesToWarm(files).map(async (f) => {
            try {
                await fetch(attachmentDownloadUrl(f.id, { disposition: "inline" }), { credentials: "same-origin" });
            } catch {
                /* offline mid-warm, or the blob is gone — try again next load */
            }
        }),
    );
}
